import { AppRoutes } from '@/constants/appRoutes'
import type { StepGuardEvalContext } from '@/hooks/createStepGuard'
import { ERunReportStep, ESearchType } from '@/pages/RunReport/types'
import type { RunReportSliceState } from '@/store/api/RunReport/RunReport.slice'

const hasSearchType = (slice: RunReportSliceState) =>
  slice.selectSearchType.searchType !== null

const isCompanySearch = (slice: RunReportSliceState) =>
  slice.selectSearchType.searchType === ESearchType.Company

const hasUseCaseAndReportType = (slice: RunReportSliceState) =>
  !!slice.useCases.selectedUseCaseId && !!slice.selectSearchType.reportType

const hasSubjectIdentity = (slice: RunReportSliceState) =>
  !!slice.subjectInfo.firstName?.trim() && !!slice.subjectInfo.lastName?.trim()

const hasCompanyIdentity = (slice: RunReportSliceState) =>
  !!slice.companyInfo.name?.trim()

type Predicate = (
  evalContext: StepGuardEvalContext<RunReportSliceState>,
) => boolean

export const STEP_PREREQUISITES: Record<ERunReportStep, Predicate> = {
  [ERunReportStep.StartSearch]: () => true,
  [ERunReportStep.SearchForm]: ({ slice }) => hasSearchType(slice),
  [ERunReportStep.SearchResults]: ({ slice }) => hasUseCaseAndReportType(slice),
  [ERunReportStep.ConfirmSubjectDetails]: ({ slice }) =>
    hasUseCaseAndReportType(slice) && hasSubjectIdentity(slice),
  [ERunReportStep.RefineSearch]: ({ slice }) =>
    hasUseCaseAndReportType(slice) && hasSubjectIdentity(slice),
  [ERunReportStep.SubjectForm]: ({ slice }) =>
    hasUseCaseAndReportType(slice) && !isCompanySearch(slice),
  [ERunReportStep.CompanyForm]: ({ slice }) =>
    hasUseCaseAndReportType(slice) && isCompanySearch(slice),
  [ERunReportStep.AddExtraDetails]: ({ slice }) =>
    !isCompanySearch(slice) && hasSubjectIdentity(slice),
  [ERunReportStep.BehaviorFlags]: ({ slice }) =>
    isCompanySearch(slice)
      ? hasCompanyIdentity(slice)
      : hasSubjectIdentity(slice),
  [ERunReportStep.Submit]: ({ slice }) =>
    isCompanySearch(slice)
      ? hasCompanyIdentity(slice)
      : hasSubjectIdentity(slice),
  [ERunReportStep.ConsentRequired]: ({ slice }) =>
    hasUseCaseAndReportType(slice) && slice.useCases.consentSwitchValue,
  [ERunReportStep.SearchStarted]: () => true,
  [ERunReportStep.GeneratingReport]: ({ searchParams }) =>
    !!searchParams.get('subjectId') && !!searchParams.get('reportId'),
}

export const STEP_FALLBACK_ROUTE: Record<ERunReportStep, AppRoutes> = {
  [ERunReportStep.StartSearch]: AppRoutes.dashboard,
  [ERunReportStep.SearchForm]: AppRoutes.dashboard,
  [ERunReportStep.SearchResults]: AppRoutes.dashboard,
  [ERunReportStep.ConfirmSubjectDetails]: AppRoutes.dashboard,
  [ERunReportStep.RefineSearch]: AppRoutes.dashboard,
  [ERunReportStep.SubjectForm]: AppRoutes.dashboard,
  [ERunReportStep.CompanyForm]: AppRoutes.dashboard,
  [ERunReportStep.AddExtraDetails]: AppRoutes.runReportBehaviorFlags,
  [ERunReportStep.BehaviorFlags]: AppRoutes.dashboard,
  [ERunReportStep.Submit]: AppRoutes.dashboard,
  [ERunReportStep.ConsentRequired]: AppRoutes.dashboard,
  [ERunReportStep.SearchStarted]: AppRoutes.dashboard,
  [ERunReportStep.GeneratingReport]: AppRoutes.dashboard,
}
